import { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { useAuth } from "../../../../context/AuthContext";
import { Info, Plus, Calendar, FileText, ExternalLink, AlertCircle, Loader2 } from "lucide-react";
import Swal from "sweetalert2";
import { 
  obtenerTodosInformacionGeneral, 
  InformacionGeneral 
} from "../../../../api/informacionGeneralService";
import DetailInformacionGeneralModal from "./DetailInformacionGeneralModal";
import CreateInformacionGeneralModal from "./CreateInformacionGeneralModal";

export default function InformacionGeneralPage() {
  const { session } = useAuth();
  const [items, setItems] = useState<InformacionGeneral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIgeCodigo, setSelectedIgeCodigo] = useState<number | null>(null);
  const isLoadingRef = useRef(false);

  const loadData = useCallback(async () => {
    if (isLoadingRef.current) return;
    
    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      const response = await obtenerTodosInformacionGeneral({
        unidad_residencial_codigo: unidadCodigo,
        codigo: "",
        activa: ""
      });

      if (response.code === "1" && response.object_) {
        setItems(response.object_);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error("Error al cargar información general:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar la información general.",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [session?.unidad?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDetail = (codigo: number) => {
    setSelectedIgeCodigo(codigo);
    setShowDetailModal(true);
  };

  const handleCloseDetail = (shouldReload: boolean) => {
    setShowDetailModal(false);
    setSelectedIgeCodigo(null);
    if (shouldReload) {
      loadData();
    }
  };

  const handleCreate = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreate = (created: boolean) => {
    setShowCreateModal(false);
    if (created) {
      loadData();
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      <Sidebar />

      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-military-600 flex items-center gap-2">
              <Info size={24} className="sm:w-8 sm:h-8" />
              Información General
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Comunicados y noticias relevantes de la unidad residencial
            </p>
          </div>
          
          <Button
            onClick={handleCreate}
            className="bg-military-500 hover:bg-military-600 text-white rounded-lg flex items-center gap-2 shadow-lg w-full sm:w-auto"
          >
            <Plus size={20} />
            Nueva Información
          </Button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-military-500 mb-4" />
            <p className="text-gray-600 animate-pulse font-medium">Cargando información...</p>
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-2 border-gray-300 bg-transparent shadow-none">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Info size={48} className="text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg font-medium">No se encontraron registros</p>
              <p className="text-gray-500 text-sm mt-1">Agrega una nueva información para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <Card 
                key={item.ige_codigo} 
                onClick={() => handleOpenDetail(item.ige_codigo)}
                className={`flex flex-col transition-all hover:shadow-xl group bg-white/80 backdrop-blur-sm cursor-pointer
                  ${item.ige_activa === 0 ? 'border-2 border-red-500 ring-2 ring-red-100 shadow-red-100' : 'border-gray-200'}`}
              >
                <CardContent className="p-5 flex-1 flex flex-col">
                  {/* Status & Date */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full
                      ${item.ige_activa === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.ige_activa_texto}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                      <Calendar size={14} className="text-military-500" />
                      {item.ige_fecha}
                    </div>
                  </div>

                  {/* Title & Desc */}
                  <div className="mb-4 flex-1">
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-military-600 transition-colors">
                      {item.ige_titulo || "Sin Título"}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-4 leading-relaxed italic">
                      {item.ige_descripcion || "Sin descripción disponible."}
                    </p>
                  </div>

                  {/* File / Footer */}
                  {item.ige_archivo_esta_cargado === "1" && (
                    <a
                      href={item.ige_ruta_archivo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg group/link hover:bg-military-50 transition-colors border border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded shadow-sm flex items-center justify-center p-1.5 border border-gray-200">
                          <img 
                            src={item.ige_tipo_archivo} 
                            alt="Tipo de archivo" 
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "https://img.easyph.com.co/iconosApp/pdf_ver.png"; // Fallback as a generic file icon
                            }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-gray-700 group-hover/link:text-military-700">Ver documento adjunto</span>
                      </div>
                      <ExternalLink size={16} className="text-gray-400 group-hover/link:text-military-500" />
                    </a>
                  )}

                  {item.ige_activa === 0 && (
                    <div className="mt-3 flex items-center gap-1.5 text-red-600 text-[10px] font-bold uppercase italic">
                      <AlertCircle size={12} />
                      Este registro no está visible para residentes
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {showDetailModal && selectedIgeCodigo && (
        <DetailInformacionGeneralModal
          isOpen={showDetailModal}
          onClose={handleCloseDetail}
          ige_codigo={selectedIgeCodigo}
        />
      )}

      {showCreateModal && (
        <CreateInformacionGeneralModal
          isOpen={showCreateModal}
          onClose={handleCloseCreate}
        />
      )}
    </div>
  );
}
